// src/pages/searchpage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from "react"; // Ensure useMemo is imported
import axios from "axios";
import Barcode from '../components/search/BarcodeScan.gif';
import ShoppingCartCheckoutIcon from '@mui/icons-material/ShoppingCartCheckout';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import { BiBarcodeReader } from 'react-icons/bi';
// import SidebarCart from "../components/sidebarcart/sidebarcart"; // OLD - Kept as fallback
// import SidebarCartV2 from "../components/sidebarcart/SidebarCartV2"; // NEW - Now global in App.js
import NewSearchTable from "../components/search/basicsearchtable"; // This is your table component

import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';

import './searchpage.css';
import { NODE_BACKEND_SCH_URL, NODE_BACKEND_API_URL } from "../config";

// --- Constants ---
const SEARCH_API_URL = NODE_BACKEND_SCH_URL;
const SET_FAVORITE_ENDPOINT = `${NODE_BACKEND_API_URL}/setisfavorite`;
const GET_CATEGORY_NAMES_URL = `${NODE_BACKEND_API_URL}/getcategorynames`;

// --- Alert component for Snackbar ---
const Alert = React.forwardRef(function Alert(props, ref) {
    return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});
Alert.displayName = 'SnackbarAlert';

function SearchPageBasic() {
    const { interceptorsReady } = useAuth();
    const searchInputRef = useRef(null);
    const barcodeInputRef = useRef(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [showResultsTable, setShowResultsTable] = useState(false);
    const [isLoadingSearch, setIsLoadingSearch] = useState(false);

    const [includeStockedOnly, setIncludeStockedOnly] = useState(false);
    const [displayingStockColumn, setDisplayingStockColumn] = useState(true);  // Show stock column by default
    const [showStockColumn, setShowStockColumn] = useState(true);  // Toggle for stock column visibility

    const [includeFavoritesOnly, setIncludeFavoritesOnly] = useState(false);

    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('All categories');
    const [isLoadingCategories, setIsLoadingCategories] = useState(false);

    const [isSidebarOpen, setIsSidebarOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const [tableSelectedItems, setTableSelectedItems] = useState([]);

    // Barcode modal states
    const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
    const [barcodeInput, setBarcodeInput] = useState('');
    const [isLoadingBarcodeSearch, setIsLoadingBarcodeSearch] = useState(false);

    const {
        cartDetails,
        isLoadingCart,
        totalCartItemsCount,
        toggleItemInCart,
        removeItemFromCart,
        updateItemQuantity,
        clearCart,
    } = useCart();

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setSnackbarOpen(false);
    };

    const showSnackbar = useCallback((message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    }, []);

    const handleToggleTableItem = useCallback((productId, isSelected) => {
        setTableSelectedItems(currentSelectedItems => {
            if (isSelected) {
                if (!currentSelectedItems.includes(productId)) {
                    return [...currentSelectedItems, productId];
                }
            } else {
                return currentSelectedItems.filter(id => id !== productId);
            }
            return currentSelectedItems;
        });
    }, []);

    const handleToggleAllTableItems = useCallback((select, allProductIds) => {
        if (select) {
            setTableSelectedItems(prevSelected => [...new Set([...prevSelected, ...allProductIds])]);
        } else {
            setTableSelectedItems(prevSelected => prevSelected.filter(id => !allProductIds.includes(id)));
        }
    }, []);

    const handleClearTableSelection = useCallback(() => {
        setTableSelectedItems([]);
        showSnackbar("Selección de tabla limpiada.", "info");
    }, [showSnackbar]);

    const handleAddSelectedToCart = useCallback(() => {
        if (tableSelectedItems.length === 0) {
            showSnackbar("No hay productos seleccionados en la tabla para agregar.", "warning");
            return;
        }

        tableSelectedItems.forEach(productId => {
            const productToAdd = searchResults.find(p => p.ProductId === productId);
            if (productToAdd) {
                 toggleItemInCart(productToAdd.ProductId, 1);
            }
        });

        setTableSelectedItems([]);
        showSnackbar(`${tableSelectedItems.length} productos agregados al carrito.`, "success");

        // Auto-focus back to search input for continuous scanning
        setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus();
                // Optional: Clear the search input for immediate new search
                // setSearchTerm('');
                console.log("Auto-focused search input after adding to cart");
            }
        }, 100); // Small delay to ensure snackbar renders first

    }, [tableSelectedItems, searchResults, toggleItemInCart, showSnackbar]);

    const fetchSearchResults = useCallback(async (currentSearchTerm, currentIncludeStockedOnly, currentIncludeFavoritesOnly, currentCategoryName) => {
        if (!currentSearchTerm.trim()) {
            setSearchResults([]);
            setShowResultsTable(false);
            setDisplayingStockColumn(false);
            return;
        }

        console.log(`Fetching search results for "${currentSearchTerm}" with stock filter: ${currentIncludeStockedOnly}, favorites filter: ${currentIncludeFavoritesOnly}, category: ${currentCategoryName}`);

        setIsLoadingSearch(true);
        setShowResultsTable(false);
        setTableSelectedItems([]);

        try {
            let apiUrl = `${SEARCH_API_URL}?search=${encodeURIComponent(currentSearchTerm)}&includeStockedOnly=${currentIncludeStockedOnly}&includeFavoritesOnly=${currentIncludeFavoritesOnly}`;

            if (currentCategoryName && currentCategoryName !== 'All categories') {
                apiUrl += `&categoryName=${encodeURIComponent(currentCategoryName)}`;
            }

            const response = await axios.get(apiUrl);
            setSearchResults(response.data);
            setDisplayingStockColumn(showStockColumn);  // Show stock column based on toggle, not filter
            setShowResultsTable(true);
        } catch (error) {
            console.error('Error fetching search data:', error);
            setSearchResults([]);
            setDisplayingStockColumn(false);
            setShowResultsTable(true);
            showSnackbar("Error al realizar la búsqueda. Intente de nuevo.", "error");
        } finally {
            setIsLoadingSearch(false);
            // Autofocus back to search input after search completes
            setTimeout(() => {
                if (searchInputRef.current) {
                    const inputElement = searchInputRef.current.querySelector('input') || searchInputRef.current;
                    inputElement.focus();
                    // Select all text so user can immediately type new search
                    inputElement.select();
                    console.log("Auto-focused search input after search completion");
                }
            }, 100);
        }
    }, [showSnackbar]);

    const handleSubmit = useCallback((event) => {
        event.preventDefault();
        fetchSearchResults(searchTerm, includeStockedOnly, includeFavoritesOnly, selectedCategory);
    }, [searchTerm, includeStockedOnly, includeFavoritesOnly, selectedCategory, fetchSearchResults]);

    const handleInputChange = useCallback((event) => {
        setSearchTerm(event.target.value);
    }, []);

    const handleStockToggleChange = useCallback((event) => {
        const isChecked = event.target.checked;
        setIncludeStockedOnly(isChecked);
        fetchSearchResults(searchTerm, isChecked, includeFavoritesOnly, selectedCategory);
    }, [searchTerm, includeFavoritesOnly, selectedCategory, fetchSearchResults]);

    // Toggle for showing/hiding stock column (independent of filter)
    const handleStockColumnToggle = useCallback((event) => {
        const isChecked = event.target.checked;
        setShowStockColumn(isChecked);
        setDisplayingStockColumn(isChecked);
    }, []);

    const handleFavoritesToggleChange = useCallback((event) => {
        const isChecked = event.target.checked;
        setIncludeFavoritesOnly(isChecked);
        fetchSearchResults(searchTerm, includeStockedOnly, isChecked, selectedCategory);
    }, [searchTerm, includeStockedOnly, selectedCategory, fetchSearchResults]);

    const handleCategoryChange = useCallback((event) => {
        const newCategory = event.target.value;
        setSelectedCategory(newCategory);
        fetchSearchResults(searchTerm, includeStockedOnly, includeFavoritesOnly, newCategory);
    }, [searchTerm, includeStockedOnly, includeFavoritesOnly, fetchSearchResults]);

    const toggleSidebar = useCallback(() => {
        setIsSidebarOpen(!isSidebarOpen);
    }, [isSidebarOpen]);

    const handleToggleFavorite = useCallback(async (productId, currentIsFavorite) => {
        const newIsFavoriteStatus = !currentIsFavorite;

        try {
            console.log(`Attempting to toggle favorite for ProductId: ${productId} to ${newIsFavoriteStatus}`);

            const response = await axios.post(SET_FAVORITE_ENDPOINT, {
                productId: productId
            });

            if (response.status === 200) {
                showSnackbar(newIsFavoriteStatus ? 'Producto agregado a favoritos.' : 'Producto removido de favoritos.', "success");
                console.log(`Favorite status updated successfully for ProductId: ${productId} to ${newIsFavoriteStatus}`);

                // If 'Solo Favoritos' is active and we just unfavorited a product, re-fetch
                if (includeFavoritesOnly) {
                    fetchSearchResults(searchTerm, includeStockedOnly, includeFavoritesOnly, selectedCategory);
                } else {
                    setSearchResults(prevResults =>
                        prevResults.map(product =>
                            product.ProductId === productId
                                ? { ...product, IsFavorite: newIsFavoriteStatus }
                                : product
                        )
                    );
                }
            } else {
                console.error('Failed to toggle favorite status:', response.data);
                showSnackbar('Fallo al actualizar el estado de favorito.', "error");
            }
        } catch (error) {
            console.error('Error toggling favorite status:', error);
            let errorMessage = 'Error al actualizar el estado de favorito. Intente de nuevo.';
            if (error.response && error.response.status === 401) {
                errorMessage = 'No autorizado: Por favor, inicie sesión de nuevo.';
            } else if (error.response && error.response.data && error.response.data.message) {
                errorMessage = `Error: ${error.response.data.message}`;
            }
            showSnackbar(errorMessage, "error");
        }
    }, [showSnackbar, searchTerm, includeStockedOnly, includeFavoritesOnly, selectedCategory, fetchSearchResults]);

    // Barcode modal handlers
    const handleOpenBarcodeModal = useCallback(() => {
        setIsBarcodeModalOpen(true);
        setBarcodeInput('');
        // Autofocus the barcode input field after modal opens
        setTimeout(() => {
            if (barcodeInputRef.current) {
                barcodeInputRef.current.focus();
            }
        }, 300); // Increased delay to wait for modal animation
    }, []);

    const handleCloseBarcodeModal = useCallback(() => {
        setIsBarcodeModalOpen(false);
        setBarcodeInput('');
        setIsLoadingBarcodeSearch(false);
    }, []);

    const handleBarcodeInputChange = useCallback((event) => {
        setBarcodeInput(event.target.value);
    }, []);

    const handleBarcodeSearch = useCallback(async () => {
        if (!barcodeInput.trim()) {
            showSnackbar("Ingrese al menos un código de barras.", "warning");
            return;
        }

        setIsLoadingBarcodeSearch(true);

        try {
            // Split the barcodes by new lines and filter out empty lines
            const barcodes = barcodeInput
                .split('\n')
                .map(code => code.trim())
                .filter(code => code.length > 0);

            if (barcodes.length === 0) {
                showSnackbar("No se encontraron códigos de barras válidos.", "warning");
                setIsLoadingBarcodeSearch(false);
                return;
            }

            console.log(`Searching for ${barcodes.length} barcodes:`, barcodes);

            const response = await axios.post(`${NODE_BACKEND_API_URL}/getbatchsearch`, {
                eans: barcodes
            });

            // Transform the response data to match the expected field names
            const transformedData = response.data.map(item => ({
                ...item,
                BrandName: item.Brand // Map 'Brand' to 'BrandName' for table compatibility
            }));

            setSearchResults(transformedData);
            setDisplayingStockColumn(false);
            setShowResultsTable(true);
            setTableSelectedItems([]);
            
            showSnackbar(`Búsqueda por lotes completada. ${response.data.length} productos encontrados.`, "success");
            handleCloseBarcodeModal();

            // Autofocus back to search input after barcode search completes
            setTimeout(() => {
                if (searchInputRef.current) {
                    const inputElement = searchInputRef.current.querySelector('input') || searchInputRef.current;
                    inputElement.focus();
                    // Select all text so user can immediately type new search
                    inputElement.select();
                    console.log("Auto-focused search input after barcode search completion");
                }
            }, 200); // Slightly longer delay to account for modal closing

        } catch (error) {
            console.error('Error en búsqueda por lotes:', error);
            showSnackbar("Error al realizar la búsqueda por lotes. Intente de nuevo.", "error");
        } finally {
            setIsLoadingBarcodeSearch(false);
        }
    }, [barcodeInput, showSnackbar, handleCloseBarcodeModal]);

    useEffect(() => {
        if (!interceptorsReady) return;
        
        // Small delay to ensure interceptors are fully attached
        const timer = setTimeout(() => {
            const fetchCategories = async () => {
                setIsLoadingCategories(true);
                try {
                    const response = await axios.get(GET_CATEGORY_NAMES_URL);
                    setCategories(response.data);
                } catch (error) {
                    console.error('Error fetching categories:', error);
                    showSnackbar("Error al cargar las categorías disponibles.", "error");
                } finally {
                    setIsLoadingCategories(false);
                }
            };
            fetchCategories();
        }, 100);
        
        return () => clearTimeout(timer);
    }, [showSnackbar, interceptorsReady]);


    // --- NEW: Memoized and conditionally sorted results for the table ---
    const sortedSearchResultsForTable = useMemo(() => {
        // Only apply default "favorites first" sort if no specific favorite or stock filters are active
        const shouldApplyDefaultSort = !includeFavoritesOnly && !includeStockedOnly;

        if (!shouldApplyDefaultSort) {
            // If any specific filter is active, return the searchResults as-is
            // The table's internal sorting will then take over based on user clicks
            return searchResults;
        }

        // Apply the "favorites first" sort
        const sorted = [...searchResults].sort((a, b) => {
            // Convert IsFavorite to a boolean if it's not already (e.g., 0 or 1 from backend)
            const isAFavorite = a.IsFavorite === true || a.IsFavorite === 1;
            const isBFavorite = b.IsFavorite === true || b.IsFavorite === 1;

            if (isAFavorite && !isBFavorite) {
                return -1; // A (favorite) comes before B (not favorite)
            }
            if (!isAFavorite && isBFavorite) {
                return 1; // B (favorite) comes before A (not favorite)
            }
            // If both are favorite or both are not favorite, sort by ProductDescription
            return String(a.ProductDescription).localeCompare(String(b.ProductDescription));
        });

        return sorted;
    }, [searchResults, includeFavoritesOnly, includeStockedOnly]);


    const sidebarCartItems = cartDetails;

    return (
        <div className={`search-main-div ${isSidebarOpen ? 'cart-sidebar-open' : ''}`}>
            <div className="parent-div">

                {/* Cart drawer is now global in App.js - no need to render here */}
                {/* Removed old toggle button and drawer components */}

                <div className="top-grid">
                    <Box className="search-controls-container" sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        paddingX: '1rem'
                    }}>
                        <form onSubmit={handleSubmit}>
                            <Box sx={{ display: 'flex', gap: 1, width: '40vw', paddingY: '1rem' }}>
                                <TextField
                                    ref={searchInputRef}
                                    label="¿Qué estás buscando?"
                                    variant="outlined"
                                    fullWidth
                                    onChange={handleInputChange}
                                    value={searchTerm}
                                    disabled={isLoadingSearch}
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    type="submit"
                                    disabled={isLoadingSearch || !searchTerm.trim()}
                                >
                                    Buscar
                                </Button>
                                <IconButton
                                    color="primary"
                                    onClick={handleOpenBarcodeModal}
                                    disabled={isLoadingSearch}
                                    sx={{ 
                                        border: '1px solid #1976d2',
                                        borderRadius: '4px',
                                        padding: '8px'
                                    }}
                                >
                                    <BiBarcodeReader size={20} />
                                </IconButton>
                            </Box>
                        </form>

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <FormControl sx={{ minWidth: 150 }} size="small" disabled={isLoadingCategories || isLoadingSearch}>
                                <InputLabel id="category-select-label">Categoría</InputLabel>
                                <Select
                                    labelId="category-select-label"
                                    id="category-select"
                                    value={selectedCategory}
                                    label="Categoría"
                                    onChange={handleCategoryChange}
                                >
                                    <MenuItem value="All categories">Todas las categorías</MenuItem>
                                    {isLoadingCategories ? (
                                        <MenuItem disabled>Cargando categorías...</MenuItem>
                                    ) : (
                                        categories.length > 0 ? (
                                            categories.map((categoryObject, index) => (
                                                <MenuItem key={index} value={categoryObject.CategoryName}>
                                                    {categoryObject.CategoryName}
                                                </MenuItem>
                                            ))
                                        ) : (
                                            <MenuItem disabled>No hay categorías</MenuItem>
                                        )
                                    )}
                                </Select>
                            </FormControl>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={showStockColumn}
                                        onChange={handleStockColumnToggle}
                                        name="stockColumnDisplay"
                                        color="success"
                                        disabled={isLoadingSearch}
                                    />
                                }
                                label="Ver Stock"
                                labelPlacement="end"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={includeStockedOnly}
                                        onChange={handleStockToggleChange}
                                        name="stockFilter"
                                        color="primary"
                                        disabled={isLoadingSearch}
                                    />
                                }
                                label="Solo c/Stock"
                                labelPlacement="end"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={includeFavoritesOnly}
                                        onChange={handleFavoritesToggleChange}
                                        name="favoritesFilter"
                                        color="secondary"
                                        disabled={isLoadingSearch}
                                    />
                                }
                                label="Destacado"
                                labelPlacement="end"
                            />
                        </Box>
                    </Box>

                    <div className="bottom-div">
                        <div className="tablediv">
                            {isLoadingSearch && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                                    <CircularProgress />
                                </Box>
                            )}

                            {!isLoadingSearch && showResultsTable && searchResults.length > 0 ? (
                                <NewSearchTable
                                    data={sortedSearchResultsForTable}
                                    hasStockData={displayingStockColumn}
                                    selectedItems={tableSelectedItems}
                                    onToggleItem={handleToggleTableItem}
                                    onToggleAllItems={handleToggleAllTableItems}
                                    onAddToCart={handleAddSelectedToCart}
                                    onClearSelection={handleClearTableSelection}
                                    currentSearchTerm={searchTerm}
                                    onToggleFavorite={handleToggleFavorite}
                                />
                            ) : (
                                !isLoadingSearch && showResultsTable && searchResults.length === 0 ? (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        No se encontraron resultados para "{searchTerm}".
                                    </Alert>
                                ) : (
                                    !isLoadingSearch && (
                                        <div className="barcode-div">
                                            <img id='barcode' src={Barcode} alt="Barcode" />
                                        </div>
                                    )
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Barcode Search Modal */}
            <Modal
                open={isBarcodeModalOpen}
                onClose={handleCloseBarcodeModal}
                aria-labelledby="barcode-modal-title"
                aria-describedby="barcode-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '500px',
                    bgcolor: 'background.paper',
                    border: '2px solid #000',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px'
                }}>
                    <Typography id="barcode-modal-title" variant="h6" component="h2" sx={{ mb: 2 }}>
                        Búsqueda por Códigos de Barras
                    </Typography>
                    <Typography id="barcode-modal-description" variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                        Ingrese los códigos de barras:
                    </Typography>
                    
                    <TextField
                        ref={barcodeInputRef}
                        autoFocus
                        fullWidth
                        multiline
                        rows={8}
                        variant="outlined"
                        value={barcodeInput}
                        onChange={handleBarcodeInputChange}
                        placeholder="Código de barras 1&#10;Código de barras 2&#10;Código de barras 3&#10;..."
                        disabled={isLoadingBarcodeSearch}
                        sx={{ mb: 3 }}
                    />
                    
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseBarcodeModal}
                            disabled={isLoadingBarcodeSearch}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleBarcodeSearch}
                            disabled={isLoadingBarcodeSearch || !barcodeInput.trim()}
                            startIcon={isLoadingBarcodeSearch && <CircularProgress size={20} />}
                        >
                            {isLoadingBarcodeSearch ? 'Buscando...' : 'Buscar'}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </div>
    );
}

export default SearchPageBasic;